<script setup>
import { computed } from 'vue'

import {
  BUTTON_ICON_DENSITIES,
  BUTTON_SIZES_NEXT
} from '@/utils/components-configurations/app-button'
import { includesValue } from '@/utils/prop-validators'

import AppIcon from '@/components/ui/AppIcon/AppIcon'
import LoadingCircle from '@/components/ui/LoadingCircle/LoadingCircle'

defineOptions({
  name: 'AppButtonIcon'
})

const props = defineProps({
  loading: {
    type: Boolean
  },

  iconName: {
    type: String,
    default: ''
  },

  width: {
    type: [String, Number],
    default: ''
  },

  height: {
    type: [String, Number],
    default: ''
  },

  iconDensity: {
    type: String,
    default: BUTTON_ICON_DENSITIES.DEFAULT,
    validator: v => includesValue(v, BUTTON_ICON_DENSITIES)
  },

  buttonSize: {
    type: String,
    default: BUTTON_SIZES_NEXT.MD,
    validator: v => includesValue(v, BUTTON_SIZES_NEXT)
  },

  buttonHeight: {
    type: Number,
    required: true
  }
})

const { XS, SM, MD, LG, XL, XXL } = BUTTON_SIZES_NEXT

const MIN_ICON_SIZE = 20
const DEFAULT_ICON_SIZE = 24

const getIconSize = ({ property, size }) => {
  if (props.iconDensity === BUTTON_ICON_DENSITIES.FIT) {
    return props.buttonHeight
  }

  if (property) return property

  if ([SM, XS, MD].includes(size)) return MIN_ICON_SIZE

  return DEFAULT_ICON_SIZE
}

const iconSizes = computed(() => ({
  width: getIconSize({ property: props.width, size: props.buttonSize }),
  height: getIconSize({ property: props.height, size: props.buttonSize })
}))

const spinnerSize = computed(() => {
  const sizeCoefficient = {
    [XS]: 6,
    [SM]: 8,
    [MD]: 10,
    [LG]: 12,
    [XL]: 14,
    [XXL]: 16
  }

  if (props.iconDensity === BUTTON_ICON_DENSITIES.DEFAULT) {
    return iconSizes.value.height - sizeCoefficient[XS]
  }

  const coefficient = sizeCoefficient[props.buttonSize]

  if (!coefficient) {
    throw new Error(`Seems that you passed incorrect size prop: ${props.buttonSize}`)
  }

  return iconSizes.value.height - coefficient
})
</script>

<template>
  <LoadingCircle
    v-if="loading"
    :style="{
      '--lc-spinner-size': `${spinnerSize}px`,
      '--lc-wrapper-size': `${iconSizes.height}px`
    }"
    class="abi-Loader"
    color="var(--abn-color)"
    size="custom"
  />
  <AppIcon
    v-else-if="iconName"
    :height="iconSizes.height"
    :icon-name
    :width="iconSizes.width"
    class="abi-Icon"
  />
</template>

<style lang="scss" scoped>
.abi-Loader,
.abi-Icon {
  flex-shrink: 0;
}

.abi-Icon {
  transform: var(--abi-transform, none);
  transition: var(--abi-transition, none);
  color: var(--abi-color, inherit);
}
</style>
